import {useCallback, useEffect, useState} from 'react';
import {useGeolocation} from 'presentation/hooks/useGeolocation';
import {usePermissions} from 'presentation/hooks/usePermissions';
import {useGetReverseGeocodeQuery} from 'presentation/pages/private/profile/queries/useGetReverseGeocodeQuery';
import {UseReverseGeolocationProps} from './types';

const REVERSE_GEOCODE_POSITION_OPTIONS: PositionOptions = {
	timeout: 15000,
	enableHighAccuracy: false,
	maximumAge: 60000,
};

export function useReverseGeolocation({
	autoRequestOnMount = false,
}: UseReverseGeolocationProps = {}) {
	const [hasAutoRequested, setHasAutoRequested] = useState(false);
	const {
		getPosition,
		resetCoords,
		coords,
		loading: geoLoading,
		error: geolocationError,
	} = useGeolocation();
	const {
		permission,
		checkPermission,
		error: permissionError,
	} = usePermissions({
		permissionName: 'geolocation',
	});

	const latitude = coords?.lat?.toString() ?? '';
	const longitude = coords?.lng?.toString() ?? '';

	const {
		data: reverseGeocodeData,
		refetch: refetchReverseGeocode,
		error: reverseGeocodeError,
		isFetching: isReverseGeocodeFetching,
	} = useGetReverseGeocodeQuery(latitude, longitude);

	useEffect(() => {
		checkPermission();
	}, [checkPermission]);

	const requestLocation = useCallback(() => {
		resetCoords();
		getPosition(REVERSE_GEOCODE_POSITION_OPTIONS);
	}, [getPosition, resetCoords]);

	useEffect(() => {
		if (!autoRequestOnMount || permission !== 'granted' || hasAutoRequested) {
			return;
		}

		setHasAutoRequested(true);
		requestLocation();
	}, [autoRequestOnMount, hasAutoRequested, permission, requestLocation]);

	useEffect(() => {
		if (coords?.lat && coords?.lng) {
			refetchReverseGeocode();
		}
	}, [coords, refetchReverseGeocode]);

	const resetLocation = useCallback(() => {
		resetCoords();
		setHasAutoRequested(false);
	}, [resetCoords]);

	return {
		permission,
		permissionError,
		coords,
		reverseGeocodeData,
		geolocationError,
		reverseGeocodeError,
		geoLoading,
		isReverseGeocodeFetching,
		isLoading: geoLoading || isReverseGeocodeFetching,
		requestLocation,
		resetLocation,
	};
}
